import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import JsBarcode from 'jsbarcode';
import * as XLSX from 'xlsx';
import InventoryReportPrint from '../components/InventoryReportPrint';

const Products = () => {
    const { authFetch, depos } = useContext(AppContext);
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [selectedDepo, setSelectedDepo] = useState('all');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isPrintOpen, setIsPrintOpen] = useState(false);

    // Form States
    const [form, setForm] = useState({ name: '', sku: '', category: '', unit: '', origin: '', barcode: '', price: '', cost_price: '', vat_rate: 0.19 });
    const [importData, setImportData] = useState([]);
    const [importLoading, setImportLoading] = useState(false);

    // Editing & Printing Labels
    const [editProduct, setEditProduct] = useState(null);
    const [printProduct, setPrintProduct] = useState(null);

    const [categoriesList, setCategoriesList] = useState([]);
    const [suppliersList, setSuppliersList] = useState([]);

    const fetchCategories = () => {
        authFetch('/api/categories')
            .then(res => res.json())
            .then(data => setCategoriesList(Array.isArray(data) ? data : []))
            .catch(console.error);
    };

    const fetchSuppliers = () => {
        authFetch('/api/suppliers')
            .then(res => res.json())
            .then(data => setSuppliersList(Array.isArray(data) ? data : []))
            .catch(console.error);
    };

    const fetchProducts = () => {
        authFetch(`/api/products?depo_id=${selectedDepo}`)
            .then(res => res.json())
            .then(data => setProducts(Array.isArray(data) ? data : []))
            .catch(console.error);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchSuppliers();
    }, [selectedDepo]);

    const handleAddProduct = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch('/api/products', {
                method: 'POST',
                body: JSON.stringify(form)
            });
            const data = await res.json();
            setIsAddModalOpen(false);
            setForm({ name: '', sku: '', category: '', unit: '', origin: '', barcode: '', price: '', cost_price: '', vat_rate: 0.19 });
            fetchProducts();
            // Sync: create stock record for this product in selected warehouse
            if (data.id && selectedDepo && selectedDepo !== 'all') {
                await authFetch('/api/stock', {
                    method: 'POST',
                    body: JSON.stringify({
                        product_id: data.id,
                        depo_id: selectedDepo,
                        quantity: 0,
                        purchase_price: 0
                    })
                });
            }
        } catch (err) {
            alert(t("Failed to add"));
        }
    };

    const handleUpdateProduct = async (e) => {
        e.preventDefault();
        try {
            await authFetch(`/api/products/${editProduct.id}`, {
                method: 'PUT',
                body: JSON.stringify(editProduct)
            });
            setEditProduct(null);
            fetchProducts();
        } catch (err) {
            alert(t("Failed to update"));
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm(t("Are you sure?"))) return;
        try {
            await authFetch(`/api/products/${id}`, { method: 'DELETE' });
            fetchProducts();
        } catch (err) {
            alert(t("Delete failed"));
        }
    };

    const handleExportExcel = () => {
        const exportData = products.map(p => ({
            'Product Name': p.name,
            'Reference Number': p.sku,
            'Category': p.category || 'General',
            'Supplier / Origin': p.origin || p.supplier_origin || p.preferred_supplier || '—',
            'Barcode': p.barcode || '—',
            'Unit': p.unit || 'pcs',
            'Quantity': parseFloat(p.quantity || p.total_quantity || 0).toFixed(2),
            'Location': selectedDepo === 'all' ? 'Consolidated' : (depos.find(d => d.id == selectedDepo)?.name || 'Unknown')
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
        const fileName = `Products_${selectedDepo === 'all' ? 'All' : depos.find(d => d.id == selectedDepo)?.name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { defval: '' }); // Ensures empty cells are strings

                // Map Excel headers to expected backend format (case-insensitive fuzzy matching)
                const mappedRows = data.map(row => {
                    const keys = Object.keys(row);
                    const findKey = (searchStr) => keys.find(k => k.toLowerCase().includes(searchStr));

                    return {
                        name: row[findKey('name') || keys[0]] || '',
                        barcode: row[findKey('barcode') || findKey('sku') || findKey('ref')] || '',
                        quantity: row[findKey('qty') || findKey('quantity')] || 0,
                        price: row[findKey('price') || findKey('cost')] || 0
                    };
                }).filter(r => r.name); // drop firmly empty rows

                setImportData(mappedRows);
            } catch (error) {
                alert(t('Failed to parse Excel file. Ensure valid format.'));
                console.error(error);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleBulkImport = async (e) => {
        e.preventDefault();
        if (selectedDepo === 'all') return alert("Please select a specific warehouse to import stock into.");
        if (importData.length === 0) return alert("No valid data found to import.");

        setImportLoading(true);
        try {
            const res = await authFetch('/api/import', {
                method: 'POST',
                body: JSON.stringify({ depo_id: selectedDepo, rows: importData })
            });
            const result = await res.json();
            alert(t("Import successful") + `: ${result.imported} record(s) processed.`);
            setIsImportModalOpen(false);
            setImportData([]);
            fetchProducts();
        } catch (err) {
            alert(t("Import failed"));
        } finally {
            setImportLoading(false);
        }
    };

    const generateRandomCode = (isEdit = false) => {
        const prefix = "200";
        let randomDigits = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
        let code13 = prefix + randomDigits + "5";
        if (isEdit) setEditProduct({ ...editProduct, barcode: code13 });
        else setForm({ ...form, barcode: code13 });
    };

    const columns = [
        { header: t('Product Name'), accessor: 'name', render: row => <div className="leading-tight"><p className="font-bold text-gray-800">{row.name}</p><p className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">{row.sku}</p></div> },
        { 
            header: t('Supplier / Origin'), 
            accessor: 'origin', 
            render: row => {
                const val = row.origin || row.supplier_origin || row.preferred_supplier || '—';
                return (
                    <span className="text-xs font-semibold text-indigo-700 bg-indigo-50/80 px-2.5 py-1 rounded-md border border-indigo-100 uppercase tracking-wide">
                        {val}
                    </span>
                );
            } 
        },
        { header: t('Category'), accessor: 'category', render: row => <span className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-100 uppercase font-medium text-gray-500">{row.category || 'General'}</span> },
        {
            header: selectedDepo === 'all' ? t('Total Global Stock') : t('Stock in') + ` ${depos.find(d => d.id == selectedDepo)?.name}`,
            accessor: 'quantity',
            render: row => {
                const qtyValue = selectedDepo === 'all' ? parseFloat(row.total_quantity || 0) : parseFloat(row.quantity || 0);
                return (
                    <span className={`text-sm font-black ${qtyValue > 0 ? 'text-blue-600' : 'text-red-400'}`}>
                        {qtyValue.toFixed(0)}
                    </span>
                );
            }
        },
        { header: t('Barcode'), accessor: 'barcode', render: row => <span className="font-mono text-[10px] text-gray-400 bg-gray-50 p-1 px-2 rounded">{row.barcode || '—'}</span> },
        { header: t('Cost'), accessor: 'cost_price', render: row => <span className="text-xs text-blue-700 font-bold">{row.cost_price ? parseFloat(row.cost_price).toFixed(2) : '0.00'}</span> },
        { header: t('Price'), accessor: 'price', render: row => <span className="text-xs text-gray-700 font-bold">{row.price ? parseFloat(row.price).toFixed(2) : '0.00'}</span> },
        { 
            header: t('Delta/Margin'), 
            accessor: 'margin', 
            render: row => {
                const cost = parseFloat(row.cost_price || 0);
                const price = parseFloat(row.price || 0);
                const margin = price - cost;
                return (
                    <span className={`text-xs font-black px-2 py-0.5 rounded ${margin > 0 ? 'bg-emerald-100 text-emerald-700' : margin < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {margin > 0 ? '+' : ''}{margin.toFixed(2)}
                    </span>
                );
            }
        },
        { header: t('Expiry Date'), accessor: 'expiry_date', render: row => <span className="text-xs text-gray-500">{row.expiry_date || '—'}</span> },
        { 
            header: t('Waste'), 
            accessor: 'waste_quantity', 
            render: row => (
                <span className="text-xs font-bold text-red-600">
                    {parseFloat(row.waste_quantity || 0).toFixed(0)}
                </span>
            )
        },
        { 
            header: t('Loss %'), 
            accessor: 'loss_percent', 
            render: row => {
                const stock = parseFloat(row.quantity || row.total_quantity || 0);
                const waste = parseFloat(row.waste_quantity || 0);
                const total = stock + waste;
                const percent = total > 0 ? (waste / total) * 100 : 0;
                return (
                    <span className={`text-xs font-black px-2 py-0.5 rounded ${percent > 10 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                        {percent.toFixed(1)}%
                    </span>
                );
            }
        },
        {
            header: t('Actions'),
            accessor: 'actions',
            render: row => (
                <div className="flex space-x-1 rtl:space-x-reverse">
                    <button onClick={() => setEditProduct(row)} title={t("Edit")} className="p-2 hover:bg-indigo-50 rounded-lg transition text-indigo-600">📌</button>
                    <button onClick={() => setPrintProduct(row)} title={t("Print")} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-600">🖨️</button>
                    <button onClick={() => handleDeleteProduct(row.id)} title={t("Delete")} className="p-2 hover:bg-red-50 rounded-lg transition text-red-500">🗑️</button>
                </div>
            )
        }
    ];

    return (
        <div className="product-page">
            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        .print-area, .print-area * { visibility: visible; }
                        .print-area { position: absolute; left: 0; top: 0; width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
                        @page { size: 50mm 30mm; margin: 0; }
                    }
                `}
            </style>

            <div className={isPrintOpen ? 'hidden' : ''}>
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200 no-print">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('Inventory Catalog')}</h2>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Branch / Warehouse')}:</span>
                            <select
                                value={selectedDepo}
                                onChange={e => setSelectedDepo(e.target.value)}
                                className="bg-gray-50 text-gray-800 text-xs font-semibold rounded-lg px-3 py-1.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 outline-none transition cursor-pointer"
                            >
                                <option value="all">🌐 {t('Total Global Inventory')}</option>
                                {depos && depos.map(d => <option key={d.id} value={d.id}>🏢 {d.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setIsImportModalOpen(true)} className="bg-white text-gray-700 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 transition shadow-sm">
                            📦 {t('Bulk Import')}
                        </button>
                        <button onClick={handleExportExcel} className="bg-white text-gray-700 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 transition shadow-sm">
                            📊 {t('Export Excel')}
                        </button>
                        <button onClick={() => setIsPrintOpen(true)} className="bg-white text-emerald-700 px-3.5 py-2 rounded-lg text-sm font-medium border border-emerald-200 hover:bg-emerald-50 flex items-center gap-1.5 transition shadow-sm">
                            📄 {t('Print Report')}
                        </button>
                        <button onClick={() => setIsAddModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition flex items-center gap-1.5 shadow-sm">
                            + {t('New Product')}
                        </button>
                    </div>
                </div>

                {/* Main Table Content */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 no-print">
                    <DataTable columns={columns} data={products} />
                </div>
            </div>

            {/* Print Inventory Report Overlay */}
            {isPrintOpen && (
                <InventoryReportPrint
                    data={products}
                    depoName={selectedDepo === 'all' ? 'Consolidated Total' : depos.find(d => d.id == selectedDepo)?.name}
                    onClose={() => setIsPrintOpen(false)}
                />
            )}

            {/* Bulk Import Modal */}
            <Modal isOpen={isImportModalOpen} onClose={() => { setIsImportModalOpen(false); setImportData([]); }} title={t('Import Stock via Excel')}>
                <div className="space-y-4">
                    <p className="text-[10px] text-gray-400 uppercase font-bold leading-tight bg-yellow-50 p-3 border-l-4 border-yellow-400">
                        {t('Warning')}: {t('This will add products and update stock for')} <b>{depos.find(d => d.id == selectedDepo)?.name || t('ALL (Not allowed)')}</b>. <br />
                        {t('Format Details')}: {t('File must be an Excel spreadsheet (.xlsx or .xls) with column headers like "Name", "Barcode" (or SKU/Ref), "Quantity", and "Price".')}
                    </p>

                    <div className="border-2 border-dashed border-indigo-200 rounded-xl p-6 text-center hover:bg-indigo-50 transition cursor-pointer relative">
                        <input
                            type="file"
                            accept=".xlsx, .xls, .csv"
                            onChange={handleFileUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <p className="font-bold text-indigo-700">{importData.length > 0 ? t('File Loaded Successfully!') : t('Click or Drag Excel File Here')}</p>
                        <p className="text-xs text-indigo-400 mt-1">{importData.length > 0 ? `${importData.length} ${t('rows ready for import')}` : t('Supports .xlsx, .xls')}</p>
                    </div>

                    {importData.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border rounded-xl bg-gray-50">
                            <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-xs">
                                <thead className="bg-gray-100 text-gray-500 sticky top-0">
                                    <tr>
                                        <th className="p-2 font-bold uppercase">{t('Name')}</th>
                                        <th className="p-2 font-bold uppercase">{t('Barcode')}</th>
                                        <th className="p-2 font-bold uppercase">{t('Qty')}</th>
                                        <th className="p-2 font-bold uppercase">{t('Price')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importData.slice(0, 10).map((r, i) => (
                                        <tr key={i} className="border-b last:border-0 border-gray-100">
                                            <td className="p-2 font-bold text-gray-800">{r.name}</td>
                                            <td className="p-2 font-mono text-gray-500">{r.barcode}</td>
                                            <td className="p-2 text-indigo-600 font-bold">{r.quantity}</td>
                                            <td className="p-2 text-emerald-600 font-medium">{r.price}</td>
                                        </tr>
                                    ))}
                                    {importData.length > 10 && (
                                        <tr>
                                            <td colSpan="4" className="p-3 text-center text-gray-400 italic">... {t('and')} {importData.length - 10} {t('more rows')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
</div>
                        </div>
                    )}

                    <button
                        onClick={handleBulkImport}
                        disabled={importLoading || selectedDepo === 'all' || importData.length === 0}
                        className="w-full bg-indigo-600 text-white py-4 rounded-xl font-black uppercase shadow hover:bg-indigo-700 disabled:opacity-50 transition"
                    >
                        {importLoading ? t('Processing...') : `${t('Execute Bulk Import')} (${importData.length} ${t('Items')})`}
                    </button>
                </div>
            </Modal>

            {/* Add Product Modal (Catalog Only) */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('New Product Catalog Entry')}>
                <form 
                    onSubmit={handleAddProduct} 
                    onKeyDown={(e) => { 
                        if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                            e.preventDefault();
                        }
                    }}
                    className="space-y-4"
                >
                    <div><label className="text-xs font-bold uppercase text-gray-400">{t('Full Product Name')}</label><input required className="w-full border-2 border-gray-100 p-3 rounded-xl focus:border-indigo-500 transition outline-none font-bold" onChange={e => setForm({ ...form, name: e.target.value })} value={form.name} /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500">{t('Reference Number')}</label>
                            <input required className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none font-mono text-sm" onChange={e => setForm({ ...form, sku: e.target.value })} value={form.sku} />
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-bold uppercase text-gray-500">{t('Category / Rayon')}</label>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        const newCat = window.prompt(t('Enter new category/rayon (e.g. Rayon 1):'));
                                        if (newCat && newCat.trim()) {
                                            const trimmed = newCat.trim();
                                            authFetch('/api/categories', { method: 'POST', body: JSON.stringify({ name: trimmed }) })
                                                .then(r => r.json())
                                                .then(data => {
                                                    const finalName = data.name || trimmed;
                                                    setCategoriesList(prev => [...prev.filter(c => c.name.toLowerCase() !== finalName.toLowerCase()), { id: data.id || Date.now(), name: finalName }]);
                                                    setForm(f => ({ ...f, category: finalName }));
                                                });
                                        }
                                    }}
                                    className="text-[11px] text-indigo-600 hover:underline font-semibold"
                                >
                                    + {t('New Rayon')}
                                </button>
                            </div>
                            <input 
                                list="categories-datalist-add" 
                                className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none text-sm" 
                                placeholder="Rayon 1 / Food / Beverage..."
                                onChange={e => setForm({ ...form, category: e.target.value })} 
                                value={form.category} 
                            />
                            <datalist id="categories-datalist-add">
                                {categoriesList.map(c => <option key={c.id || c.name} value={c.name} />)}
                            </datalist>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase text-gray-500">{t('Supplier / Origin')} (e.g. France, Liban, Rayan, Grandcafe)</label>
                        <input 
                            list="suppliers-datalist-add"
                            className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none font-semibold text-sm uppercase" 
                            onChange={e => setForm({ ...form, origin: e.target.value })} 
                            value={form.origin} 
                            placeholder="France / Liban / Rayan / Grandcafe..." 
                        />
                        <datalist id="suppliers-datalist-add">
                            {suppliersList.map(s => <option key={s.id || s.name} value={s.name} />)}
                            <option value="France" />
                            <option value="Liban" />
                            <option value="Rayan" />
                            <option value="Grandcafe" />
                        </datalist>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500">{t('Barcode')}</label>
                            <div className="flex gap-2">
                                <input className="w-full border border-gray-200 p-2.5 rounded-lg font-mono focus:border-indigo-500 outline-none text-sm" placeholder={t('Scan or type...')} value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} />
                                <button type="button" onClick={() => generateRandomCode(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 rounded-lg font-bold text-xs uppercase transition">{t('Generate')}</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold uppercase text-gray-500">{t('Cost Price')}</label>
                                <input type="number" step="0.01" min="0" className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none text-sm" placeholder="0.00" onChange={e => setForm({ ...form, cost_price: e.target.value })} value={form.cost_price} />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase text-gray-500">{t('Selling Price')}</label>
                                <input type="number" step="0.01" min="0" className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none text-sm" placeholder="0.00" onChange={e => setForm({ ...form, price: e.target.value })} value={form.price} />
                            </div>
                        </div>
                    </div>
                    <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 font-bold rounded-xl mt-4 shadow-sm transition">{t('Add to Catalog')}</button>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={!!editProduct} onClose={() => setEditProduct(null)} title={t('Update Catalog Entry')}>
                {editProduct && (
                    <form 
                        onSubmit={handleUpdateProduct} 
                        onKeyDown={(e) => { 
                            if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                                e.preventDefault();
                            }
                        }}
                        className="space-y-4"
                    >
                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500">{t('Product Name')}</label>
                            <input required className="w-full border border-gray-200 p-2.5 rounded-lg font-bold text-sm" onChange={e => setEditProduct({ ...editProduct, name: e.target.value })} value={editProduct.name || ''} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold uppercase text-gray-500">{t('Reference Number')}</label>
                                <input required className="w-full border border-gray-200 p-2.5 rounded-lg font-mono text-sm" onChange={e => setEditProduct({ ...editProduct, sku: e.target.value })} value={editProduct.sku || ''} />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-xs font-bold uppercase text-gray-500">{t('Category / Rayon')}</label>
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            const newCat = window.prompt(t('Enter new category/rayon (e.g. Rayon 1):'));
                                            if (newCat && newCat.trim()) {
                                                const trimmed = newCat.trim();
                                                authFetch('/api/categories', { method: 'POST', body: JSON.stringify({ name: trimmed }) })
                                                    .then(r => r.json())
                                                    .then(data => {
                                                        const finalName = data.name || trimmed;
                                                        setCategoriesList(prev => [...prev.filter(c => c.name.toLowerCase() !== finalName.toLowerCase()), { id: data.id || Date.now(), name: finalName }]);
                                                        setEditProduct(f => ({ ...f, category: finalName }));
                                                    });
                                            }
                                        }}
                                        className="text-[11px] text-indigo-600 hover:underline font-semibold"
                                    >
                                        + {t('New Rayon')}
                                    </button>
                                </div>
                                <input 
                                    list="categories-datalist-edit"
                                    className="w-full border border-gray-200 p-2.5 rounded-lg text-sm" 
                                    placeholder="Rayon 1 / Food / Beverage..."
                                    onChange={e => setEditProduct({ ...editProduct, category: e.target.value })} 
                                    value={editProduct.category || ''} 
                                />
                                <datalist id="categories-datalist-edit">
                                    {categoriesList.map(c => <option key={c.id || c.name} value={c.name} />)}
                                </datalist>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500">{t('Supplier / Origin')} (e.g. France, Liban, Rayan, Grandcafe)</label>
                            <input 
                                list="suppliers-datalist-edit"
                                className="w-full border border-gray-200 p-2.5 rounded-lg font-semibold text-sm uppercase" 
                                onChange={e => setEditProduct({ ...editProduct, origin: e.target.value })} 
                                value={editProduct.origin || ''} 
                                placeholder="France / Liban / Rayan / Grandcafe..." 
                            />
                            <datalist id="suppliers-datalist-edit">
                                {suppliersList.map(s => <option key={s.id || s.name} value={s.name} />)}
                                <option value="France" />
                                <option value="Liban" />
                                <option value="Rayan" />
                                <option value="Grandcafe" />
                            </datalist>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold uppercase text-gray-500">{t('Barcode')}</label>
                                <div className="flex gap-2">
                                    <input
                                        className="w-full border border-gray-200 p-2.5 rounded-lg font-mono focus:border-indigo-500 outline-none text-sm"
                                        placeholder={t('Enter barcode...')}
                                        value={editProduct.barcode || ''}
                                        onChange={e => setEditProduct({ ...editProduct, barcode: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => generateRandomCode(true)}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 rounded-lg font-bold text-xs uppercase transition"
                                    >
                                        {t('New Code')}
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold uppercase text-gray-500">{t('Cost Price')}</label>
                                    <input type="number" step="0.01" min="0" className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none text-sm" placeholder="0.00" onChange={e => setEditProduct({ ...editProduct, cost_price: e.target.value })} value={editProduct.cost_price || ''} />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase text-gray-500">{t('Selling Price')}</label>
                                    <input type="number" step="0.01" min="0" className="w-full border border-gray-200 p-2.5 rounded-lg focus:border-indigo-500 outline-none text-sm" placeholder="0.00" onChange={e => setEditProduct({ ...editProduct, price: e.target.value })} value={editProduct.price || ''} />
                                </div>
                            </div>
                        </div>
                        <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 font-bold rounded-xl mt-4 shadow-sm transition">{t('Save Changes')}</button>
                    </form>
                )}
            </Modal>

            {/* Print Label Modal */}
            <Modal isOpen={!!printProduct} onClose={() => setPrintProduct(null)} title={t('Barcode Label Preview')}>
                <div className="flex flex-col items-center py-6">
                    <div className="print-area bg-white p-6 border-2 border-dashed border-gray-200 rounded-lg text-center min-w-[250px]">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-900 truncate mb-1 w-48 mx-auto">{printProduct?.name}</p>

                        {printProduct?.barcode ? (
                            <BarcodeGenerator value={printProduct.barcode} />
                        ) : (
                            <div className="py-4 bg-red-50 rounded border border-red-100 mb-2">
                                <p className="text-[10px] text-red-500 font-bold uppercase">{t('No Barcode Assigned')}</p>
                                <button
                                    onClick={async () => {
                                        const code = "200" + Math.floor(Math.random() * 1000000000).toString().padStart(9, '0') + "5";
                                        try {
                                            await authFetch(`/api/products/${printProduct.id}`, {
                                                method: 'PUT',
                                                body: JSON.stringify({ ...printProduct, barcode: code })
                                            });
                                            setPrintProduct({ ...printProduct, barcode: code });
                                            fetchProducts();
                                        } catch (e) {
                                            alert(t("Failed to assign code automatically"));
                                        }
                                    }}
                                    className="mt-2 text-[9px] bg-indigo-600 text-white px-3 py-1 rounded-full font-black uppercase animate-pulse"
                                >
                                    {t('Fix & Generate Now')}
                                </button>
                            </div>
                        )}

                        <p className="text-[8px] font-bold text-gray-400 mt-1">{printProduct?.sku}</p>
                    </div>

                    {printProduct?.barcode && (
                        <button onClick={() => {
                            const svgHtml = document.querySelector('.print-area')?.outerHTML || '';
                            const html = `<!DOCTYPE html><html><head><title>Print Barcode</title>
                            <style>
                                @page { size: 50mm 30mm; margin: 0; }
                                body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; width: 50mm; height: 30mm; background: white; overflow: hidden; }
                                .print-area { text-align: center; width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; }
                                p { margin: 0; font-family: sans-serif; font-size: 10px; font-weight: 900; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 90%; }
                                .mt-1 { margin-top: 2px !important; }
                                svg { max-width: 100%; height: auto; display: block; }
                            </style>
                            </head><body>
                            ${svgHtml}
                            <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
                            </body></html>`;
                            const popup = window.open('', '_blank', 'width=450,height=300');
                            if (popup) { popup.document.write(html); popup.document.close(); }
                            else alert('Please allow popups for this site to print.');
                        }} className="mt-8 bg-gray-900 text-white px-8 py-3 rounded-2xl font-black uppercase hover:bg-black transition shadow-lg flex items-center gap-2 no-print">
                            🖨️ {t('PRINT TO THERMAL')}
                        </button>
                    )}
                </div>
            </Modal>
        </div>
    );
};

const BarcodeGenerator = ({ value }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (value && ref.current) {
            try {
                // Try EAN13 first, fall back to CODE128 if invalid check digit
                let format = "CODE128";
                if (/^\\d{13}$/.test(value)) {
                    try {
                        JsBarcode(ref.current, value, {
                            format: "EAN13", width: 2, height: 50, displayValue: true, fontSize: 14, margin: 0, background: "#ffffff",
                            valid: function(valid){ if(!valid) throw new Error("Invalid EAN"); }
                        });
                        return; // Success
                    } catch(e) {
                        // fallback below
                    }
                }
                JsBarcode(ref.current, value, {
                    format, width: 2, height: 50, displayValue: true, fontSize: 14, margin: 0, background: "#ffffff"
                });
            } catch (e) {
                console.warn("Barcode rendering failed for value:", value, e);
            }
        }
    }, [value]);

    if (!value) return null;
    return (
        <div className="flex justify-center bg-white p-2">
            <svg ref={ref}></svg>
        </div>
    );
};

export default Products;

